import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { 
  Check,
  CreditCard,
  Shield,
  ArrowRight
} from "lucide-react";

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { token, refreshUser, user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState("annual");
  const [loading, setLoading] = useState(false);

  const plans = [
    {
      id: "monthly",
      name: "Monthly",
      price: 75,
      period: "month",
      description: "Perfect for trying us out",
      features: [
        "Digital Membership Card",
        "10% Discount at Partners",
        "Catch Logging",
        "Community Access",
        "Q&A Support"
      ]
    },
    {
      id: "annual",
      name: "Annual",
      price: 500,
      period: "year",
      description: "Best value - Save R400!",
      badge: "Save R400",
      features: [
        "Everything in Monthly",
        "2 Months FREE",
        "Priority Support",
        "Early Access to Events",
        "Exclusive Competitions"
      ],
      popular: true
    }
  ];

  const handleSubscribe = async () => {
    setLoading(true);

    try {
      // For demo, we'll use the demo activation endpoint
      // In production, this would redirect to PayFast
      const response = await axios.post(`${API}/subscriptions/activate-demo`, 
        { plan_type: selectedPlan },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      toast.success("Subscription activated successfully!");
      await refreshUser();
      navigate("/dashboard");

      // Production PayFast flow (commented out for demo):
      // const response = await axios.post(`${API}/subscriptions/initiate`,
      //   { plan_type: selectedPlan },
      //   { headers: { Authorization: `Bearer ${token}` }}
      // );
      // 
      // const form = document.createElement('form');
      // form.method = 'POST';
      // form.action = response.data.payfast_url;
      // 
      // Object.entries(response.data.payfast_data).forEach(([key, value]) => {
      //   const input = document.createElement('input');
      //   input.type = 'hidden';
      //   input.name = key;
      //   input.value = value;
      //   form.appendChild(input);
      // });
      // 
      // document.body.appendChild(form);
      // form.submit();

    } catch (error) {
      toast.error("Failed to process subscription");
    } finally {
      setLoading(false);
    }
  };

  if (user?.subscription_status === "active") {
    return (
      <Layout title="Subscription" subtitle="Manage your membership">
        <div className="max-w-md mx-auto">
          <Card className="bg-white card-shadow border-0">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--success)]/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-[var(--success)]" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--deep-ocean)] mb-2">You're Already a Member!</h2>
              <p className="text-[var(--river-stone)] mb-6">
                Your {user.subscription_type} subscription is active until{" "}
                {user.subscription_end ? new Date(user.subscription_end).toLocaleDateString() : "indefinitely"}.
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                className="btn-primary"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Choose Your Plan" subtitle="Start your FishingFanatix membership today">
      <div className="max-w-4xl mx-auto">
        {/* Plan Selection */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {plans.map((plan) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: plan.id === "annual" ? 0.1 : 0 }}
            >
              <Card 
                className={`relative cursor-pointer transition-all h-full ${
                  selectedPlan === plan.id
                    ? plan.popular
                      ? "bg-[var(--deep-ocean)] ring-4 ring-[var(--sunrise-orange)]"
                      : "bg-white ring-4 ring-[var(--deep-ocean)]"
                    : "bg-white card-shadow border-0 hover:card-shadow-deep"
                }`}
                onClick={() => setSelectedPlan(plan.id)}
                data-testid={`plan-${plan.id}`}
              >
                {plan.badge && (
                  <Badge className="absolute -top-3 right-4 bg-[var(--sunrise-orange)] text-[var(--deep-ocean)]">
                    {plan.badge}
                  </Badge>
                )}
                <CardContent className="p-8">
                  <div className={`flex items-center gap-3 mb-2 ${
                    selectedPlan === plan.id && plan.popular ? "text-white" : "text-[var(--deep-ocean)]"
                  }`}>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === plan.id 
                        ? plan.popular 
                          ? "border-[var(--sunrise-orange)] bg-[var(--sunrise-orange)]" 
                          : "border-[var(--deep-ocean)] bg-[var(--deep-ocean)]"
                        : "border-gray-300"
                    }`}>
                      {selectedPlan === plan.id && (
                        <Check className={`w-4 h-4 ${plan.popular ? "text-[var(--deep-ocean)]" : "text-white"}`} />
                      )}
                    </div>
                    <h3 className="text-2xl font-bold">{plan.name}</h3>
                  </div>
                  
                  <p className={`text-sm mb-4 ${
                    selectedPlan === plan.id && plan.popular ? "text-white/70" : "text-[var(--river-stone)]"
                  }`}>
                    {plan.description}
                  </p>
                  
                  <div className="mb-6">
                    <span className={`text-4xl font-extrabold ${
                      selectedPlan === plan.id && plan.popular ? "text-white" : "text-[var(--deep-ocean)]"
                    }`}>
                      R{plan.price}
                    </span>
                    <span className={selectedPlan === plan.id && plan.popular ? "text-white/70" : "text-[var(--river-stone)]"}>
                      /{plan.period}
                    </span>
                  </div>
                  
                  <ul className="space-y-3">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2">
                        <Check className={`w-5 h-5 ${
                          selectedPlan === plan.id && plan.popular 
                            ? "text-[var(--sunrise-orange)]" 
                            : "text-[var(--success)]"
                        }`} />
                        <span className={
                          selectedPlan === plan.id && plan.popular 
                            ? "text-white/90" 
                            : "text-[var(--river-stone)]"
                        }>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Subscribe Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center"
        >
          <Button
            onClick={handleSubscribe}
            className="btn-accent text-lg px-12 py-6"
            disabled={loading}
            data-testid="subscribe-btn"
          >
            {loading ? (
              <span className="spinner" />
            ) : (
              <>
                Subscribe Now <ArrowRight className="ml-2 w-5 h-5" />
              </>
            )}
          </Button>
          
          <div className="flex items-center justify-center gap-2 mt-4 text-sm text-[var(--river-stone)]">
            <Shield className="w-4 h-4" />
            <span>Secure payment powered by PayFast</span>
          </div>

          <p className="mt-4 text-xs text-[var(--river-stone)]">
            * For demo purposes, clicking subscribe will activate your membership immediately without payment.
          </p>
        </motion.div>
      </div>
    </Layout>
  );
};

export default SubscriptionPage;
